import { Anchor, Box, Heading, Icon, IconButton, Paragraph, Text } from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';
import { useContext } from 'react';
import {
  useRenterProfileHeaderDataQuery,
  useRenterProfileHeader_GetConnectedExperienceQuery,
} from '../../../../types/GraphqlTypes';
import { InboxPageMobileView } from '../../../../types/InboxPageMobileView';
import useInboxRouterHelper from '../../../../hooks/useInboxRouterHelper';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import conversationUtils from '../../../../utils/conversationUtils';
import { logApolloError } from '../../../../utils/errorUtils';
import { FULLSTORY_EXCLUDE } from '../../../../constants/fullstory';
import ContactActions from '../ContactActions';
import InboxSidebarHeaderSkeleton from './skeleton';

export default function Header(): JSX.Element {
  const { conversationId, listingAlias } = useContext(InboxPageContext);
  const { changeMobileView } = useInboxRouterHelper();
  const { data: headerData } = useRenterProfileHeaderDataQuery({
    variables: {
      conversationId: conversationId!,
      listingAlias: listingAlias!,
    },
    fetchPolicy: 'cache-only',
  });

  const { data: connectedExperienceData, error: connectedExperienceError } =
    useRenterProfileHeader_GetConnectedExperienceQuery({
      variables: {
        listingAlias: listingAlias || '',
      },
      fetchPolicy: 'cache-first',
    });

  if (connectedExperienceError) {
    logApolloError(connectedExperienceError, {
      details: 'RenterProfile - Header useRenterProfileHeader_GetConnectedExperienceQuery',
    });
  }
  const handleArrowClick = () => {
    changeMobileView(InboxPageMobileView.MessageList);
  };

  const listingUrlPathname = connectedExperienceData?.ZRM_getConnectedExperience.isConnectedExperience
    ? 'find-tenants'
    : 'listing';
  const listingUrl = `/rental-manager/properties/${listingAlias}/${listingUrlPathname}`;

  if (!headerData) {
    return <InboxSidebarHeaderSkeleton />;
  }

  const { inquiry, referenceName, address, isActive } = headerData.ZRM_getConversationById;
  const { isPhoneLead = false, phone = '', name = '' } = inquiry || {};
  const listingAddress = address?.split(',')[0];
  const displayName = isPhoneLead
    ? conversationUtils.getPhoneLeadParticipantDisplay({
        name: name || '',
        phoneNumber: phone || '',
        shouldFormatPhoneNumber: true,
      })
    : referenceName;

  return (
    <>
      <IconButton
        shape="square"
        emphasis="bare"
        tone="brand"
        onClick={handleArrowClick}
        icon={<Icon size="sm" render={<IconChevronLeftFilled />} />}
        title="Go back to messages"
        css={{
          xxl: {
            display: 'none',
          },
        }}
      />
      <Box
        css={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: 'default',
        }}
      >
        <Heading
          level={2}
          className={FULLSTORY_EXCLUDE}
          textStyle="heading-xs"
          css={{ textTransform: 'capitalize', textAlign: 'center' }}
        >
          {displayName}
        </Heading>

        <Paragraph
          textStyle="body-sm"
          css={{ marginBottom: 'default', marginTop: 'tightest', color: 'text.subtle', textAlign: 'center' }}
        >
          For&nbsp;
          <Anchor href={listingUrl}>{listingAddress}</Anchor>
          {!isActive && (
            <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
              &nbsp;(Off Market)
            </Text>
          )}
        </Paragraph>

        <ContactActions />
      </Box>
    </>
  );
}
